import { Component, OnInit, OnDestroy, Injector } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { Subject, of } from 'rxjs';
import { takeUntil, catchError } from 'rxjs/operators';
import { CategoryService } from '../../osm-services/category.service';
import { UserFavAppService } from '../../osm-services/user-fav-app.service';
import { AppUsageService } from '../../osm-services/app-usage.service';
import { AuthService } from '../../services/auth.service';
import { OfferService, Offer } from '../../osm-services/offer.service';
import { ImageUtils } from '../../shared/utils/image.utils';

interface SubCategory {
  subCategoryId: string;
  name: string;
  active: boolean;
  category: string;
  order: number;
}

interface FavoriteApp {
  favoriteId?: string;
  appliId: string;
  appName: string;
  subCategoryId: string;
  subCategoryName: string;
  icon: string;
  link: string;
  androidImageUrl?: string;
}

@Component({
  selector: 'app-category-detail',
  templateUrl: './category-detail.component.html',
  styleUrls: ['./category-detail.component.scss']
})
export class CategoryDetailComponent implements OnInit, OnDestroy {
  categoryId: string = '';
  categoryName: string = '';
  category: any = null;
  subcategories: SubCategory[] = [];
  userFavorites: { [subCategoryId: string]: FavoriteApp[] } = {};
  
  // Offers
  offers: Offer[] = [];
  offersLoading = false;
  
  loading = false;
  error: string | null = null;
  userId: string = '';
  
  // Accordion state
  expandedAccordions: { [subCategoryId: string]: boolean } = {};
  
  private destroy$ = new Subject<void>();
  private offerService?: OfferService; // Lazy load this

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private categoryService: CategoryService,
    private userFavAppService: UserFavAppService,
    private appUsageService: AppUsageService,
    private authService: AuthService,
    private injector: Injector  // Use injector to get OfferService later
  ) {}

  // Utility methods for consistent image handling
  getAppIcon(app: any): string {
    return ImageUtils.getAppIcon(app);
  }

  ngOnInit(): void {
    // Get user ID
    this.authService.getUser().then(user => {
      if (user && user.attributes) {
        this.userId = user.attributes.sub;
        this.loadUserFavorites();
      }
    });

    // Get category ID from route - subscribe to changes
    this.route.params
      .pipe(takeUntil(this.destroy$))
      .subscribe(params => {
        const newCategoryId = params['categoryId'];
        if (newCategoryId && newCategoryId !== this.categoryId) {
          this.categoryId = newCategoryId;
          // Reset state when category changes
          this.category = null;
          this.subcategories = [];
          this.expandedAccordions = {};
          this.offers = [];  // Reset offers
          this.loadCategoryWithSubcategories();
          // Reload offers filtered by new category
          this.loadActiveOffers();
        } else if (newCategoryId && !this.categoryId) {
          // First load - set categoryId and load offers
          this.categoryId = newCategoryId;
          this.loadCategoryWithSubcategories();
          setTimeout(() => {
            this.loadActiveOffers();
          }, 100);
        }
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private loadCategoryWithSubcategories(): void {
    this.loading = true;
    this.error = null;

    this.categoryService.getCategoryWithSubcategories(this.categoryId)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response) => {
          this.category = response.category;
          this.categoryName = response.category.name;
          
          // Filter subcategories to match only this category AND only active ones AND not deleted
          const filteredSubcategories = response.subcategories.filter((sub: SubCategory) => {
            const matchesCategory = sub.category === this.category.name || sub.category === this.category.categoryId;
            // Check both 'active' and 'isActive' fields, handle boolean and string values
            const activeValue = sub.active ?? (sub as any).isActive;
            const isActive = activeValue === true || activeValue === 'true' || activeValue === undefined;
            // Check if not deleted
            const isDeleted = (sub as any).isDeleted;
            const notDeleted = isDeleted !== true && isDeleted !== 'true';
            return matchesCategory && isActive && notDeleted;
          });
          
          this.subcategories = filteredSubcategories.sort((a, b) => a.order - b.order);
          
          // Expand all subcategories by default
          this.subcategories.forEach(subcategory => {
            this.expandedAccordions[subcategory.subCategoryId] = true;
          });
          
          this.loading = false;
        },
        error: (error) => {
          console.error('Error loading category:', error);
          this.error = 'Failed to load category details';
          this.loading = false;
        }
      });
  }

  private loadUserFavorites(): void {
    if (!this.userId) return;

    this.userFavAppService.getUserFavorites(this.userId)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response) => {
          const favorites = response.favorites || {};
          this.userFavorites = {};
          
          Object.keys(favorites).forEach(subCategoryId => {
            const favs = favorites[subCategoryId];
            if (favs && favs.length > 0) {
              this.userFavorites[subCategoryId] = favs;
            }
          });
        },
        error: (error) => {
          console.error('Error loading user favorites:', error);
        }
      });
  }

  // Load offers - EXACT SAME PATTERN as getCategoriesWithApps
  // Now filters by categoryId to show only offers for this category
  private loadActiveOffers(): void {
    this.offersLoading = true;

    try {
      // Get OfferService ONLY when we need it, not in constructor
      if (!this.offerService) {
        this.offerService = this.injector.get(OfferService);
      }

      // Pass categoryId to filter offers for this specific category
      this.offerService.getActiveOffers(this.categoryId)
        .pipe(
          takeUntil(this.destroy$),
          catchError(error => {
            console.log('Offers API call failed (this is OK):', error);
            return of({ offers: [], total: 0, success: false });
          })
        )
        .subscribe({
          next: (response) => {
            this.offers = response.offers || [];
            this.offersLoading = false;
          },
          error: (error) => {
            console.log('Offers subscription error (this is OK):', error);
            this.offers = [];
            this.offersLoading = false;
          }
        });
    } catch (error) {
      console.log('Offers try-catch error (this is OK):', error);
      this.offers = [];
      this.offersLoading = false;
    }
  }

  toggleAccordion(subCategoryId: string): void {
    this.expandedAccordions[subCategoryId] = !this.expandedAccordions[subCategoryId];
  }

  isExpanded(subCategoryId: string): boolean {
    return this.expandedAccordions[subCategoryId] || false;
  }

  hasFavorite(subCategoryId: string): boolean {
    return !!this.userFavorites[subCategoryId] && this.userFavorites[subCategoryId].length > 0;
  }

  getFavorites(subCategoryId: string): FavoriteApp[] {
    return this.userFavorites[subCategoryId] || [];
  }

  openAppSelector(subcategory: SubCategory): void {
    // Navigate to app selector page for this subcategory
    this.router.navigate(['/select-app', subcategory.subCategoryId], {
      queryParams: {
        subcategoryName: subcategory.name,
        categoryId: this.categoryId,
        categoryName: this.categoryName
      }
    });
  }

  openCategoryPageForSubcategory(subcategory: SubCategory): void {
    // Navigate to the category page with category/subcategory in URL path
    // Format: /category/{categoryName}/{subcategoryName}
    this.router.navigate(['/category', this.categoryName, subcategory.name]);
  }

  removeFavorite(subCategoryId: string, appliId?: string): void {
    if (!this.userId) return;

    const message = appliId 
      ? 'Are you sure you want to remove this favorite app?'
      : 'Are you sure you want to remove all favorite apps from this subcategory?';

    if (confirm(message)) {
      this.userFavAppService.deleteFavoriteApp(this.userId, subCategoryId, appliId)
        .pipe(takeUntil(this.destroy$))
        .subscribe({
          next: () => {
            if (appliId) {
              // Remove specific app from the array
              const favorites = this.userFavorites[subCategoryId];
              if (favorites) {
                this.userFavorites[subCategoryId] = favorites.filter(fav => fav.appliId !== appliId);
                // If no favorites left, delete the key
                if (this.userFavorites[subCategoryId].length === 0) {
                  delete this.userFavorites[subCategoryId];
                }
              }
            } else {
              // Remove all favorites for this subcategory
              delete this.userFavorites[subCategoryId];
            }
          },
          error: (error) => {
            console.error('Error removing favorite:', error);
            alert('Failed to remove favorite app');
          }
        });
    }
  }

  openApp(favorite: FavoriteApp): void {
    // Track app click for analytics
    this.trackAppUsage(favorite.appliId, favorite.appName);
    
    if (favorite.link) {
      window.open(favorite.link, '_blank');
    }
  }

  openAllApps(subCategoryId: string): void {
    const favorites = this.getFavorites(subCategoryId);
    
    if (favorites && favorites.length > 0) {
      // Open each app exactly like the individual openApp() does
      favorites.forEach((favorite) => {
        if (favorite.link) {
          // Use exact same method as openApp - no extra parameters
          window.open(favorite.link, '_blank');
          
          // Track each app click
          this.trackAppUsage(favorite.appliId, favorite.appName);
        }
      });
    }
  }

  private trackAppUsage(appId: string, appName: string): void {
    if (!this.userId || !appId) return;
    
    const usageData = {
      userId: this.userId,
      appId: appId,
      appName: appName,
      category: this.categoryName,
      subCategory: ''
    };
    
    // Call the app usage tracking API
    this.appUsageService.trackAppUsage(usageData)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response) => {
          console.log('App usage tracked:', response);
        },
        error: (error) => {
          // Silently fail - don't interrupt user experience
          console.error('Error tracking app usage:', error);
        }
      });
  }

  openOffer(offer: Offer): void {
    if (offer.link) {
      // Ensure the link has a proper protocol
      let url = offer.link.trim();
      if (url && !url.startsWith('http://') && !url.startsWith('https://')) {
        url = 'https://' + url;
      }
      if (url) {
        window.open(url, '_blank');
      }
    } else {
      console.warn('Offer has no link:', offer.title);
    }
  }

  getOfferBanner(offer: Offer): string {
    if (!this.offerService) {
      this.offerService = this.injector.get(OfferService);
    }
    return this.offerService.getOfferImageUrl(offer.banner);
  }

  handleImageError(event: Event): void {
    ImageUtils.handleImageError(event);
  }

  handleAppImageError(event: Event): void {
    ImageUtils.handleImageError(event);
  }

  handleOfferImageError(event: Event): void {
    ImageUtils.handleImageError(event);
  }
}
